// Grids & Gantts — TABLE LINES AS ROWS: the lines of a table kept in a work
// item's rich-text field, drawn as rows under that work item.
//
// A work item may carry a list as a TABLE in a rich-text field — the parts a
// manufacturing step consumes ("Component No. · Object Description ·
// Quantity · UoM …"), the lines of an order, the items of a delivery. Those
// lines are not work items, and a check that is about exactly them (is each
// part in stock?) has nothing to stand on unless they are rows. So a folder
// may say: under every work item of these types, draw the lines of this
// field's table (expand.lines — normalised in config.js).
//
// A LINE ROW IS NOT A WORK ITEM. It has no key, nothing to open, edit, drag,
// clone or reschedule, and it is never counted as work: Count, Progress,
// Count where… and the toolbar's "N work items" are exactly what they were.
// Its cells read the line through the grid's own FIELDS — `map` says that the
// field "Part Number" reads the line's "Component No.", so ONE column shows a
// work item's Part Number and a line's alike, and an External database column
// matching on Part Number looks a line's part up too.
//
// A line carries its work item's status (Hide Done hides the lines of Done
// work, and a check skips them) and its work item's Due date (what a check
// orders the lines by) — but never shows them as its own.
//
// Pure and import-free, like every file a client fork's suite may copy (see
// ../../README.md).

// Names compared the way a person reads them: case, spaces and punctuation
// ignored — "Component No." is "COMPONENT NO" is "component no".
export const sameName = (a, b) => norm(a) === norm(b) && norm(a) !== '';
const norm = (s) => String(s == null ? '' : s).toLowerCase().replace(/[^a-z0-9]/g, '');
const lc = (s) => String(s == null ? '' : s).trim().toLowerCase();

// ---- reading the tables out of a value ------------------------------------------
// A rich-text field arrives as ADF (an object, or the same as a JSON string);
// an older or plain field as text: "a | b" lines, wiki "||a||b||", markdown
// "| a | b |", or tab-separated. Every one of them answers the same shape:
//   [{ headings: ['Item', 'Component No.', …], rows: [['0010', 'A0265-…', …], …] }]
const adfText = (n) => {
  if (!n || typeof n !== 'object') return '';
  if (n.type === 'text') return String(n.text || '');
  if (n.type === 'hardBreak') return ' ';
  const inner = (n.content || []).map(adfText);
  return n.type === 'paragraph' || n.type === 'heading' ? inner.join('') : inner.join(' ');
};
function adfTables(doc) {
  const out = [];
  const walk = (n) => {
    if (!n || typeof n !== 'object') return;
    if (n.type === 'table') {
      const rows = (n.content || []).filter((r) => r && r.type === 'tableRow')
        .map((r) => ({ head: (r.content || []).some((c) => c && c.type === 'tableHeader'), cells: (r.content || []).map((c) => adfText(c).replace(/\s+/g, ' ').trim()) }));
      if (rows.length) {
        const first = rows[0];
        out.push({ headings: first.cells, rows: rows.slice(1).map((r) => r.cells), headed: first.head });
      }
      return;
    }
    (n.content || []).forEach(walk);
  };
  walk(doc);
  return out;
}
function textTables(s) {
  const out = [];
  let cur = null;
  const split = (line) => {
    if (line.includes('\t')) return line.split('\t').map((x) => x.trim());
    let t = line.trim();
    if (t.startsWith('||')) return t.replace(/^\|\|/, '').replace(/\|\|$/, '').split('||').map((x) => x.trim());
    if (t.startsWith('|')) t = t.slice(1);
    if (t.endsWith('|')) t = t.slice(0, -1);
    return t.split('|').map((x) => x.trim());
  };
  String(s).split(/\r?\n/).forEach((raw) => {
    const line = raw.trim();
    const tabular = line && (line.includes('|') || line.includes('\t'));
    if (!tabular) { cur = null; return; }
    if (/^\|?\s*:?-{2,}/.test(line)) return; // a markdown rule under the headings
    const cells = split(line);
    if (!cur) { cur = { headings: cells, rows: [] }; out.push(cur); } else cur.rows.push(cells);
  });
  return out;
}
export function tablesIn(value) {
  if (value == null || value === '') return [];
  if (typeof value === 'object') return adfTables(value);
  const s = String(value).trim();
  if (s.startsWith('{')) {
    try { return adfTables(JSON.parse(s)); } catch (e) { /* not JSON — read it as text */ }
  }
  return textTables(s);
}

// ---- a figure in a cell ---------------------------------------------------------
// What a table (or an SAP export) writes a quantity as: "33.000", "1,070.000",
// "1 070,5", "5.000-" (SAP's trailing minus), "  2". Answers a number, or null
// when the text is not a figure — a part number like "8300260" IS a figure,
// which is why cellValue below only turns DECIMALS and GROUPED figures into
// numbers and keeps whole numbers as the text they were (leading noughts and all).
export function figureOf(text) {
  if (typeof text === 'number') return Number.isFinite(text) ? text : null;
  let s = String(text == null ? '' : text).trim().replace(/ /g, ' ');
  if (!s) return null;
  let neg = false;
  if (/-$/.test(s)) { neg = true; s = s.slice(0, -1).trim(); }
  if (/^-/.test(s)) { neg = !neg; s = s.slice(1).trim(); }
  if (!/^[\d.,' ]+$/.test(s)) return null;
  s = s.replace(/[' ]/g, '');
  const dot = s.lastIndexOf('.'); const comma = s.lastIndexOf(',');
  if (dot >= 0 && comma >= 0) {
    // both: the last one is the decimal mark
    s = dot > comma ? s.replace(/,/g, '') : s.replace(/\./g, '').replace(',', '.');
  } else if (comma >= 0) {
    s = /^\d{1,3}(,\d{3})+$/.test(s) ? s.replace(/,/g, '') : s.replace(',', '.');
  } else if ((s.match(/\./g) || []).length > 1) {
    s = s.replace(/\./g, ''); // 1.070.000 — dots as thousands
  }
  const n = Number(s);
  if (!Number.isFinite(n)) return null;
  return neg ? -n : n;
}
const DECIMAL = /^-?\s*[\d,. ']*[.,]\d+\s*-?$/;
const GROUPED = /^-?\d{1,3}([, ]\d{3})+([.,]\d+)?-?$/;
export function cellValue(text) {
  const s = String(text == null ? '' : text).trim();
  if (!s) return null;
  if (DECIMAL.test(s) || GROUPED.test(s)) { const n = figureOf(s); if (n != null) return n; }
  return s;
}

// ---- one work item's lines -------------------------------------------------------
// `spec` is a folder's expand.lines (config.js normalises it):
//   { field, types, label, key, text, skip: [{ column, value }], map: [{ field, column }] }
// `fieldId` is `spec.field` resolved on this site; `fieldIdOf(name)` resolves
// the mapped fields. Answers the lines as line ITEMS — work-item-shaped, so a
// column reads them with the code it reads work items with:
//   { key, line: { of, n, cells, label, part, due }, fields: { summary, issuetype, status, … } }
export function linesOf(item, spec, { fieldId, fieldIdOf = () => '' } = {}) {
  if (!item || !spec || !fieldId) return [];
  const f = item.fields || {};
  const type = lc(f.issuetype && f.issuetype.name);
  if ((spec.types || []).length && !(spec.types || []).some((t) => lc(t) === type)) return [];
  const tables = tablesIn(f[fieldId]);
  if (!tables.length) return [];
  const want = spec.key || '';
  const table = (want && tables.find((t) => t.headings.some((h) => sameName(h, want)))) || tables[0];
  const at = (name) => (name ? table.headings.findIndex((h) => sameName(h, name)) : -1);
  const keyAt = at(spec.key); const textAt = at(spec.text);
  const skips = (spec.skip || []).map((s) => ({ i: at(s.column), v: lc(s.value) })).filter((s) => s.i >= 0);
  const maps = (spec.map || []).map((m) => ({ id: fieldIdOf(m.field), i: at(m.column) })).filter((m) => m.id && m.i >= 0);
  const label = spec.label || 'Line';
  const out = [];
  table.rows.forEach((cellsArr, ri) => {
    if (!cellsArr.some((c) => String(c || '').trim())) return;
    if (skips.some((s) => lc(cellsArr[s.i]) === s.v)) return;
    const cells = {};
    table.headings.forEach((h, hi) => { cells[h] = cellsArr[hi] == null ? '' : String(cellsArr[hi]); });
    const part = keyAt >= 0 ? String(cellsArr[keyAt] || '').trim() : '';
    const text = textAt >= 0 ? String(cellsArr[textAt] || '').trim() : '';
    const fields = {
      summary: [part, text].filter(Boolean).join(' - ') || cellsArr.filter(Boolean).join(' · '),
      issuetype: { id: 'line', name: label },
      // the work item's own: what Hide Done and a check go by — never shown as the line's
      status: f.status || null,
      statusCategory: f.statusCategory || (f.status && f.status.statusCategory) || null,
      project: f.project || null,
    };
    // A figure becomes a number ("1,070.000" → 1070) so a number column reads
    // it; the line's KEY column stays the text it is (a part number is a name).
    maps.forEach((m) => { fields[m.id] = m.i === keyAt ? (part || null) : cellValue(cellsArr[m.i]); });
    out.push({
      key: `${item.key}~${ri + 1}`,
      line: { of: item.key, n: ri + 1, cells, label, part, due: String(f.duedate || '').slice(0, 10) },
      fields,
    });
  });
  return out;
}

// Every folder's lines, once: Map(folderId → Map(work item key → lines)) and
// every line by its key. A work item two folders hold has its lines made once
// per folder that asks for them (a folder may read another table), and the
// same line key is the same line.
export function linesIndex(parts, { fieldIdOf = () => '' } = {}) {
  const byFolder = new Map();
  const all = new Map();
  (parts || []).forEach((p) => {
    const spec = p && p.folder && p.folder.expand && p.folder.expand.lines;
    if (!spec || !spec.field) return;
    const fieldId = fieldIdOf(spec.field);
    if (!fieldId) return;
    const mine = new Map();
    p.byKey.forEach((it, k) => {
      const ls = linesOf(it, spec, { fieldId, fieldIdOf });
      if (!ls.length) return;
      mine.set(k, ls);
      ls.forEach((l) => { if (!all.has(l.key)) all.set(l.key, l); });
    });
    byFolder.set(p.folder.id, mine);
  });
  return { byFolder, all };
}

// Whether a line holds the text (the search box): its summary and every cell.
export function lineHas(line, q) {
  if (!q) return true;
  const t = `${(line.fields && line.fields.summary) || ''} ${Object.values((line.line && line.line.cells) || {}).join(' ')}`.toLowerCase();
  return t.includes(q);
}

export const isLine = (x) => !!(x && x.line && x.line.of);
